import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { catalogCars } from '@/mocks/cars';

export default function Catalog() {
  const { t } = useTranslation();
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedFuel, setSelectedFuel] = useState('all');
  const [selectedTrans, setSelectedTrans] = useState('all');
  const [maxPrice, setMaxPrice] = useState(200000);
  const [minPrice, setMinPrice] = useState(20000);
  const sectionRef = useRef<HTMLDivElement>(null);

  const brands = [
    { key: 'all', label: t('catalog.filter.all') },
    { key: 'BMW', label: 'BMW' },
    { key: 'Mercedes', label: 'Mercedes' },
    { key: 'Porsche', label: 'Porsche' },
    { key: 'Audi', label: 'Audi' },
    { key: 'Volkswagen', label: 'Volkswagen' },
  ];

  const types = [
    { key: 'all', label: t('catalog.filter.all') },
    { key: 'Deportivo', label: t('catalog.filter.sport') },
    { key: 'SUV', label: t('catalog.filter.suv') },
    { key: 'Berlina', label: t('catalog.filter.sedan') },
  ];

  const fuels = [
    { key: 'all', label: t('catalog.filter.all') },
    { key: 'Gasolina', label: t('catalog.filter.gasoline') },
    { key: 'Híbrido', label: t('catalog.filter.hybrid') },
    { key: 'Eléctrico', label: t('catalog.filter.electric') },
  ];

  const transmissions = [
    { key: 'all', label: t('catalog.filter.all') },
    { key: 'Automático', label: t('catalog.filter.auto') },
    { key: 'Manual', label: t('catalog.filter.manual') },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.scroll-reveal').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 80);
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const filtered = catalogCars.filter((car) => {
    if (selectedBrand !== 'all' && car.brand !== selectedBrand) return false;
    if (selectedType !== 'all' && car.type !== selectedType) return false;
    if (selectedFuel !== 'all' && car.fuel !== selectedFuel) return false;
    if (selectedTrans !== 'all' && car.transmission !== selectedTrans) return false;
    if (car.price > maxPrice) return false;
    if (car.price < minPrice) return false;
    return true;
  });

  const handleWhatsApp = (car: typeof catalogCars[0]) => {
    const msg = encodeURIComponent(`${t('catalog.whatsapp')} ${car.brand} ${car.model} ${car.year}. ${t('catalog.whatsapp2')}`);
    window.open(`https://wa.me/34631611077?text=${msg}`, '_blank');
  };

  return (
    <section id="catalogo" ref={sectionRef} className="py-16 sm:py-28 bg-[#080508] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-purple-900/8 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-12 scroll-reveal">
          <div className="inline-flex items-center gap-2 border border-purple-500/30 px-4 py-2 rounded-full mb-6">
            <span className="text-purple-400 text-xs tracking-widest uppercase font-medium">{t('catalog.label')}</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mb-4" style={{ letterSpacing: '0.03em' }}>
            {t('catalog.title1')} <span className="gradient-text-purple">{t('catalog.title2')}</span>
          </h2>
          <p className="font-body text-white/50 text-base max-w-xl mx-auto leading-relaxed">
            {t('catalog.subtitle')}
          </p>
        </div>

        {/* Filters */}
        <div className="glass-card rounded-2xl p-4 sm:p-6 mb-8 sm:mb-10 scroll-reveal">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            {/* Brand */}
            <div>
              <label className="text-white/40 text-xs tracking-widest uppercase mb-2 block">{t('catalog.filter.brand')}</label>
              <div className="flex flex-wrap gap-2">
                {brands.map((b) => (
                  <button
                    key={b.key}
                    onClick={() => setSelectedBrand(b.key)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      selectedBrand === b.key
                        ? 'gradient-purple text-white'
                        : 'border border-white/10 text-white/50 hover:border-purple-500/40 hover:text-white/80'
                    }`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Type */}
            <div>
              <label className="text-white/40 text-xs tracking-widest uppercase mb-2 block">{t('catalog.filter.type')}</label>
              <div className="flex flex-wrap gap-2">
                {types.map((tp) => (
                  <button
                    key={tp.key}
                    onClick={() => setSelectedType(tp.key)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      selectedType === tp.key
                        ? 'gradient-purple text-white'
                        : 'border border-white/10 text-white/50 hover:border-purple-500/40 hover:text-white/80'
                    }`}
                  >
                    {tp.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Fuel */}
            <div>
              <label className="text-white/40 text-xs tracking-widest uppercase mb-2 block">{t('catalog.filter.fuel')}</label>
              <div className="flex flex-wrap gap-2">
                {fuels.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setSelectedFuel(f.key)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      selectedFuel === f.key
                        ? 'gradient-purple text-white'
                        : 'border border-white/10 text-white/50 hover:border-purple-500/40 hover:text-white/80'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Transmission */}
            <div>
              <label className="text-white/40 text-xs tracking-widest uppercase mb-2 block">{t('catalog.filter.trans')}</label>
              <div className="flex flex-wrap gap-2">
                {transmissions.map((tr) => (
                  <button
                    key={tr.key}
                    onClick={() => setSelectedTrans(tr.key)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      selectedTrans === tr.key
                        ? 'gradient-purple text-white'
                        : 'border border-white/10 text-white/50 hover:border-purple-500/40 hover:text-white/80'
                    }`}
                  >
                    {tr.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Price */}
            <div>
              <label className="text-white/40 text-xs tracking-widest uppercase mb-2 block">
                {t('catalog.filter.price')}: <span className="text-purple-400">{minPrice.toLocaleString('es-ES')}€ — {maxPrice.toLocaleString('es-ES')}€</span>
              </label>
              <div className="flex flex-col gap-2">
                <input
                  type="range"
                  min={20000}
                  max={200000}
                  step={5000}
                  value={minPrice}
                  onChange={(e) => setMinPrice(Math.min(Number(e.target.value), maxPrice - 5000))}
                  className="w-full accent-purple-500 cursor-pointer"
                />
                <input
                  type="range"
                  min={20000}
                  max={200000}
                  step={5000}
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(Math.max(Number(e.target.value), minPrice + 5000))}
                  className="w-full accent-purple-500 cursor-pointer"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Cars Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-20 text-white/30">
            <i className="ri-car-line text-5xl mb-4 block" />
            <p className="text-lg">{t('catalog.empty')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filtered.map((car, i) => (
              <div
                key={car.id}
                className="scroll-reveal glass-card rounded-2xl overflow-hidden group hover:border-purple-500/30 transition-all duration-400"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                {/* Image */}
                <div className="relative overflow-hidden h-52">
                  <img
                    src={car.image}
                    alt={`${car.brand} ${car.model}`}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 to-transparent" />
                  <div className="absolute top-3 left-3">
                    <span className="gradient-purple text-white text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full">
                      {car.badge}
                    </span>
                  </div>
                  <div className="absolute top-3 right-3 glass-card px-2 py-1 rounded-lg">
                    <span className="text-white/70 text-xs">{car.origin}</span>
                  </div>
                </div>

                {/* Info */}
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-heading text-xl text-white" style={{ letterSpacing: '0.04em' }}>
                        {car.brand} {car.model}
                      </h3>
                      <p className="font-body text-white/40 text-xs tracking-wider">{car.year}</p>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-xl gradient-text-purple" style={{ letterSpacing: '0.03em' }}>
                        {car.price.toLocaleString('es-ES')}€
                      </div>
                    </div>
                  </div>

                  {/* Specs */}
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="glass-card rounded-lg p-2 text-center">
                      <i className="ri-speed-line text-purple-400 text-sm block mb-1" />
                      <span className="text-white/60 text-xs">{car.power}</span>
                    </div>
                    <div className="glass-card rounded-lg p-2 text-center">
                      <i className="ri-dashboard-line text-purple-400 text-sm block mb-1" />
                      <span className="text-white/60 text-xs">{car.km.toLocaleString('es-ES')} km</span>
                    </div>
                    <div className="glass-card rounded-lg p-2 text-center">
                      <i className="ri-gas-station-line text-purple-400 text-sm block mb-1" />
                      <span className="text-white/60 text-xs">{car.fuel}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => handleWhatsApp(car)}
                    className="w-full gradient-purple text-white text-sm font-semibold tracking-wider uppercase py-3 rounded-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line mr-2" />
                    {t('catalog.cta')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}